import Vue from 'vue';
import UploadApi from '@/api/upload/UploadApi';
import axios from 'axios';

const state = {
  uploadedFiles: [],
  fileProgress: {},
  lastCompletedFile: null,
  isModalOpen: false
};

const getters = {
  uploadedFiles: state => state.uploadedFiles,
  fileProgress: state => state.fileProgress,
  lastCompletedFileName: state => state.lastCompletedFile,
};

const mutations = {
  ADD_UPLOADED_FILES(state, file) {
    const newFile = {
      ...file,
      status: 'pending',
    };
    state.uploadedFiles.push(newFile);
  },
  REMOVE_UPLOADED_FILE(state, fileId) {
    state.uploadedFiles = state.uploadedFiles.filter(file => file.id !== fileId);
  },
  SET_FILE_PROGRESS(state, { fileId, progress }) {
    const fileIndex = state.uploadedFiles.findIndex(file => file.id === fileId);
    if (fileIndex !== -1) {
      const file = state.uploadedFiles[fileIndex];
      file.progress = progress;
      if (progress < 100) {
        file.status = 'uploading';
      } else {
        file.status = 'completed';
      }
      state.uploadedFiles = [...state.uploadedFiles];
    }
  },
  INIT_FILE_PROGRESS(state, fileId) {
    state.fileProgress[fileId] = 0;
  },
  SET_FILE_PROGRESSING(state, fileId) {
    const fileIndex = state.uploadedFiles.findIndex(file => file.id === fileId);
    if (fileIndex !== -1) {
      Vue.set(state.uploadedFiles[fileIndex], 'status', 'progressing');
    }
  },
  SET_FILE_STATUS(state, { fileId, status }) {
    const fileIndex = state.uploadedFiles.findIndex(file => file.id === fileId);
    if (fileIndex !== -1) {
      Vue.set(state.uploadedFiles[fileIndex], 'status', status);
      if (status === 'completed') {
        state.lastCompletedFile = state.uploadedFiles[fileIndex].name;
      }
    }
  },
};

const actions = {
  addUploadedFile({ commit }, file) {
    commit('ADD_UPLOADED_FILES', file);
  },
  removeUploadedFile({ commit }, fileId) {
    commit('REMOVE_UPLOADED_FILE', fileId);
  },
  setFileProgress({ commit }, { fileId, progress }) {
    commit('SET_FILE_PROGRESS', { fileId, progress });
  },
  initFileProgress({ commit }, fileId) {
    commit('INIT_FILE_PROGRESS', fileId);
  },
  addFiles({ commit }, files) {
    const validFiles = files.filter(file => {

      const type = file.name.split('.').pop().toLowerCase();
      if(type === 'mp4' || type === 'mov'){
        const fileTypeLower = file.type.toLowerCase();
        // console.log("fileTypeLower => ", fileTypeLower)

        return fileTypeLower === 'video/mp4' || fileTypeLower === 'video/quicktime';
      } else {
        return null;
      }
      // return fileTypeLower === 'video/mp4' || fileTypeLower === 'video/quicktime';
    }).map(file => ({
      name: file.name,
      size: parseFloat((file.size / 1024 / 1024).toFixed(2)),
      type: file.type.split('/').pop().toUpperCase(),
      id: Date.now() + Math.random().toString(36).substring(2, 15),
      progress: 0,
      status: 'pending',
      file: file,
    }));
    validFiles.forEach(file => {
      commit('ADD_UPLOADED_FILES', file);
    });
  },
  async uploadFiles({ dispatch, state }) {
    for (let file of state.uploadedFiles.filter(f => f.status === 'pending')) {
      try {
        await dispatch('uploadFile', { file: file.file, fileIndex: file.id });
      } catch (error) {
        console.error('error', error);
        break;
      }
    }
  },
  async uploadFile({ commit, state, dispatch }, { file, fileIndex }) {
    if (!(file instanceof File)) {
      return;
    }

    commit('SET_FILE_STATUS', { fileId: fileIndex, status: 'uploading' });

    const chunkSize = 1024 * 1024 * 5;
    const totalChunks = Math.ceil(file.size / chunkSize);
    let isCompleted = true;

    try {
      const { uploadId, key } = await UploadApi.initiateMultipartUpload(file.name);

      let uploadPromises = [];

      for (let currentChunk = 0; currentChunk < totalChunks; currentChunk++) {
        const partNumber = currentChunk + 1;
        const start = currentChunk * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const fileChunk = file.slice(start, end);

        //console.log('key', key);
        const presignedUrl = await UploadApi.getPresignedUrl(key, uploadId, partNumber);

        const uploadPromise = axios.put(presignedUrl, fileChunk, {
          headers: {
            'Content-Type': file.type
          }
        }).then(response => {
          const etag = response.headers['etag'] || response.headers['ETag'];
          commit('SET_FILE_PROGRESS', { fileId: fileIndex, progress: Math.round(((currentChunk + 1) / totalChunks) * 100) });
          return { PartNumber: partNumber, ETag: etag };
        }).catch(() => {
          isCompleted = false;
          commit('SET_FILE_STATUS', { fileId: fileIndex, status: 'failed' });
          dispatch('snackbar/showSnackbar', {
            message: `${file.name} 파일 업로드 실패`,
            error: true
          }, { root: true });
        });

        uploadPromises.push(uploadPromise);
      }

      const parts = await Promise.all(uploadPromises);

      if (isCompleted) {
        await UploadApi.completeMultipartUpload(key, uploadId, parts, file.name);

        commit('SET_FILE_STATUS', { fileId: fileIndex, status: 'completed' });
        dispatch('snackbar/showSnackbar', {
          message: `${file.name} 파일 업로드 성공`,
          error: false
        }, { root: true });
      }

    } catch (error) {
      console.error("Upload failed", error);
      commit('SET_FILE_STATUS', { fileId: fileIndex, status: 'failed' });
      dispatch('snackbar/showSnackbar', {
        message: `${file.name} 파일 업로드 실패`,
        error: true
      }, { root: true });
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};